/* eslint-disable no-console */
import axios, {
  AxiosError,
  AxiosHeaderValue,
  AxiosInstance,
  AxiosRequestConfig,
  HeadersDefaults,
  InternalAxiosRequestConfig,
} from "axios";
import jwt_decode from "jwt-decode";

import LOCAL_STORAGE from "@/constants/local-storage";
import { logSessionEvent } from "@/utils/session-log";
import ROUTE_URLS from "@/constants/route-urls";
import { isImpersonationSession } from "@/helpers/auth-helpers";
import { IDecodedToken } from "@/interfaces/auth.interfaces";
import { isTokenExpired } from "@/utils/token-utils";
import { refreshSession } from "@/auth/token-refresh-manager";
import { markUserActivity } from "@/utils/session-activity";

export type TRequestData = Record<never, unknown>;

export type TRequestHeaders = HeadersDefaults & {
  [key: string]: AxiosHeaderValue;
};

const isPublicRoute = (pathname: string) =>
  pathname.includes("/login") ||
  pathname.includes("/register") ||
  pathname.includes("/signup") ||
  pathname.includes("/terms") ||
  pathname.includes("/privacy");

const forceLogout = (reason = "desconhecido") => {
  // Em rota pública não há sessão a encerrar: evita loop de logout/redirect.
  if (isPublicRoute(window.location.pathname)) return;

  const hadSession =
    Boolean(localStorage.getItem(LOCAL_STORAGE.accessTokenKey)) ||
    Boolean(localStorage.getItem(LOCAL_STORAGE.refreshTokenKey));

  if (!hadSession) return;

  logSessionEvent(`forceLogout (http-client): ${reason}`);
  localStorage.removeItem(LOCAL_STORAGE.accessTokenKey);
  localStorage.removeItem(LOCAL_STORAGE.refreshTokenKey);
  window.dispatchEvent(new CustomEvent("tokenExpired"));

  window.location.href = "/login";
};



export class HttpClient {
  private axiosInstance: AxiosInstance;

  constructor(private baseUrl: string) {
    this.axiosInstance = axios.create({ baseURL: baseUrl });
    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.axiosInstance.interceptors.request.use(async (config) => {
      const isAuthRoute = config.url?.includes("/login") || config.url?.includes("/register");
      const isInternalLogin = config.data?.login && config.data?.password;

      if (isAuthRoute && !isInternalLogin) {
        return config;
      }

      let accessToken = localStorage.getItem(LOCAL_STORAGE.accessTokenKey);
      const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

      if (!accessToken) {
        return config;
      }

      try {
        let decodedToken: IDecodedToken = jwt_decode(accessToken);

        if (isTokenExpired(decodedToken) && refreshToken) {
          const outcome = await refreshSession();
          if (outcome.status === "success") {
            accessToken = outcome.accessToken;
            decodedToken = jwt_decode(outcome.accessToken);
          }
        }

        if (!isTokenExpired(decodedToken) && accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
          config.headers.CustomerId = decodedToken.CustomerId || "";
        }
      } catch (error) {
        console.error("Error decoding token:", error);
      }

      return config;
    });

    this.axiosInstance.interceptors.response.use(
      (response) => {
        markUserActivity();
        return response;
      },
      async (error: AxiosError) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        if (!error.response || error.response.status !== 401 || originalRequest._retry) {
          return Promise.reject(error);
        }

        if (originalRequest.url?.includes("/refreshtoken")) {
          forceLogout("401 na chamada de refreshtoken");
          return Promise.reject(error);
        }

        // Impersonation com JWT válido: 401 de permissão não encerra a sessão.
        try {
          const accessToken = localStorage.getItem(LOCAL_STORAGE.accessTokenKey);
          if (accessToken) {
            const decodedToken: IDecodedToken = jwt_decode(accessToken);
            if (isImpersonationSession(decodedToken) && !isTokenExpired(decodedToken)) {
              return Promise.reject(error);
            }
          }
        } catch {
          // segue para fluxo de refresh / logout
        }

        originalRequest._retry = true;

        const refreshToken = localStorage.getItem(LOCAL_STORAGE.refreshTokenKey);

        if (!refreshToken) {
          forceLogout("401 sem refreshToken");
          return Promise.reject(error);
        }

        const outcome = await refreshSession();

        if (outcome.status !== "success") {
          // Falha temporária (rede, timeout, 5xx) preserva a sessão.
          if (outcome.status === "invalid") {
            forceLogout(`refresh inválido apos 401 (${outcome.reason})`);
          }
          return Promise.reject(error);
        }

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${outcome.accessToken}`;
        }

        return this.axiosInstance(originalRequest);
      }
    );
  }


  public buildHeaders(_accessToken?: string) {
    let accessToken: string | null | undefined = _accessToken;
    if (!_accessToken) {
      accessToken = localStorage ? localStorage.getItem(LOCAL_STORAGE.accessTokenKey) : "";
    }

    let customerId: string | null = null;
    if (accessToken) {
      try {
        const decodedToken: IDecodedToken = jwt_decode(accessToken);
        customerId = decodedToken.CustomerId;
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      Accept: "application/json",
      "Content-Type": "application/json",
      CustomerId: customerId || "",
    };

    return headers;
  }

  async request<T = never, R = TRequestData>(
    method: string,
    path: string,
    body?: R,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const headers = this.buildHeaders();

    const response = await this.axiosInstance.request<T>({
      method,
      url: path,
      data: body,
      headers,
      ...config,
    });

    return response.data;
  }

  async post<T = never>(
    path: string,
    body?: TRequestData,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("post", path, body, {
      ...config,
      headers: { ...headers, ...config?.headers },
    });
  }

  async put<T = never>(path: string, body?: TRequestData, config?: AxiosRequestConfig): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("put", path, body, {
      ...config,
      headers: { ...headers, ...config?.headers },
    });
  }

  async get<T = never>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("get", path, undefined, {
      ...config,
      headers: { ...headers, ...config?.headers },
    });
  }

  async getWithResponse<T = never>(
    path: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: T; status: number }> {
    const headers = this.buildHeaders();
    const response = await this.axiosInstance.request<T>({
      method: "get",
      url: path,
      headers: { ...headers, ...config?.headers },
      ...config,
    });
    return { data: response.data, status: response.status };
  }

  async delete<T = never>(path: string, config?: AxiosRequestConfig): Promise<T> {
    const headers = this.buildHeaders();
    return this.request<T>("delete", path, undefined, {
      ...config,
      headers: { ...headers, ...config?.headers },
    });
  }
}

const apiBaseUrl = import.meta.env.VITE_API_URL || "https://api-qas.talk2buy.com.br";
const httpRequest = new HttpClient(apiBaseUrl);
export default httpRequest;
